import { SHHtmlBlock, SHStack, SHTypography } from "@components/design-systems";
import {
  BusinessMetricCategories,
  BusinessMetricDTO,
} from "@models/platform-analysis/entities/steps/businessMetric";
import { useTheme } from "@mui/material";
import { updateBusinessMetricItemSelectionStore } from "@redux/slices/platform-analysis";
import { useAppDispatch } from "@redux/store";
import { hexToRGBA } from "@utils";
import { memo } from "react";

type AnalysisBusinessMetricItemProps = {
  metricData?: BusinessMetricDTO;
  categoryKey?: keyof BusinessMetricCategories;
};

export const AnalysisBusinessMetricItem = memo(
  ({ metricData, categoryKey }: AnalysisBusinessMetricItemProps) => {
    const { palette } = useTheme();
    const dispatch = useAppDispatch();
    const { id, isSelected, name, description } = metricData || {};

    const handleOnClick = () => {
      if (!id || !categoryKey) return;
      dispatch(
        updateBusinessMetricItemSelectionStore({
          id: id,
          isSelected: !isSelected,
          category: categoryKey,
        }),
      );
    };

    return (
      <SHStack
        width={"100%"}
        border={`1px solid ${
          isSelected ? palette.success[100] : palette.common.black
        }`}
        bgcolor={isSelected ? palette.success[50] : palette.common.white}
        color={isSelected ? palette.text.primary : palette.text.third}
        boxShadow={`3px 3px 4px ${hexToRGBA(palette.common.black, 0.25)}`}
        padding={"5%"}
        direction={"column"}
        gap={1}
        sx={{
          transition: "all .3s linear",
          cursor: "pointer",
          "&:hover": {
            border: `1px solid ${palette.primary[800]}`,
            boxShadow: "none",
          },
          "&:active": {
            boxShadow: `inset 3px 3px 4px ${hexToRGBA(
              palette.common.black,
              0.25,
            )}`,
            transition: "all .1s linear",
            "&>svg>path": {
              fill: palette.common.white,
            },
          },
        }}
        onClick={handleOnClick}
      >
        <SHTypography variant="subtitle1" lineHeight={1.2}>
          {name}
        </SHTypography>
        <SHHtmlBlock content={description} variant="body2" />
      </SHStack>
    );
  },
  (prevProps, nextProps) => {
    const { isSelected: prevIsSelected } = prevProps.metricData || {};
    const { isSelected: nextIsSelected } = nextProps.metricData || {};
    return prevIsSelected === nextIsSelected;
  },
);
